import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  SafeAreaView,
  StatusBar,
  KeyboardAvoidingView,
} from 'react-native';
import { 
  Button, 
  Input,
  PhoneInput,
  Logo,
  DualPaneLayout,
  IOSRestrictionMessage,
  colors, 
  spacing, 
  typography 
} from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useTheme } from '../contexts/ThemeContext';

export const BusinessSetupScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { themeMode } = useTheme();
  const { user, logout, updateTokens, updateAuthTokens, setUserBusinesses, setCurrentBusiness } = useAppStore();
  const { showNotification, navigateTo } = useAppContext();
  
  const [setupMode, setSetupMode] = useState<'choose' | 'create' | 'join'>('create');
  const [isLoading, setIsLoading] = useState(false);
  
  // Business creation form
  const [businessForm, setBusinessForm] = useState({
    name: '',
    email: user?.email || '',
    phone: '',
    address: '',
    industry: '',
    description: ''
  });
  
  // Phone input ref to get formatted number with country code
  const phoneInputRef = React.useRef<any>(null);

  // Debug: Log form state changes
  useEffect(() => {
    console.log('🔍 [BusinessSetup] Business form state changed:', businessForm);
  }, [businessForm]);
  
  // Join business form
  const [invitationCode, setInvitationCode] = useState('');
  
  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateBusinessForm = (field: string, value: string) => {
    console.log(`🔍 [BusinessSetup] Updating form field '${field}' with value:`, value);
    setBusinessForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateBusinessForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!businessForm.name.trim()) {
      newErrors.name = 'Business name is required';
    }
    
    if (!businessForm.email.trim()) {
      newErrors.email = 'Business email is required';
    } else if (!/\S+@\S+\.\S+/.test(businessForm.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!businessForm.phone.trim()) {
      newErrors.phone = 'Business phone is required';
    }
    
    if (!businessForm.industry.trim()) {
      newErrors.industry = 'Industry is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateInvitationCode = (): boolean => {
    if (!invitationCode.trim()) {
      setErrors({ invitationCode: 'Invitation code is required' });
      return false;
    }
    
    if (invitationCode.trim().length !== 8) {
      setErrors({ invitationCode: 'Invitation code must be 8 characters' });
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleCreateBusiness = async () => {
    console.log('🔍 [BusinessSetup] Starting business creation...');
    console.log('🔍 [BusinessSetup] Form data:', businessForm);
    console.log('🔍 [BusinessSetup] Business name from form:', businessForm.name);
    console.log('🔍 [BusinessSetup] Business email from form:', businessForm.email);

    if (!validateBusinessForm()) {
      console.log('❌ [BusinessSetup] Form validation failed');
      return;
    }

    console.log('✅ [BusinessSetup] Form validation passed');
    setIsLoading(true);

    try {
      // Get formatted phone number with country code
      let phoneToSend = businessForm.phone;
      if (phoneInputRef.current) {
        const phoneData = phoneInputRef.current.getValue();
        if (phoneData?.formattedNumber) {
          phoneToSend = phoneData.formattedNumber;
          console.log('📞 [BusinessSetup] Using formatted phone with country code:', phoneToSend);
        }
      }
      
      // Prepare data to send
      const businessData = {
        ...businessForm,
        phone: phoneToSend
      };
      
      console.log('🚀 [BusinessSetup] Calling API service...');
      console.log('📞 [BusinessSetup] Phone number being sent:', phoneToSend);
      const response = await apiService.createBusiness(businessData);
      console.log('📊 [BusinessSetup] API response received:', response);

      if (response.statusCode === 1000 && response.data) {
        console.log('✅ [BusinessSetup] Business created successfully!');
        console.log('📊 [BusinessSetup] Business data received:', response.data);
        console.log('📊 [BusinessSetup] Business name from response:', response.data.name);
        console.log('🔑 [BusinessSetup] Response has newToken?:', !!response.data.newToken);
        if (response.data.newToken) {
          console.log('🔑 [BusinessSetup] newToken preview:', response.data.newToken.substring(0, 50) + '...');
        }
        
        // Update user state with the new business
        const newBusiness = response.data;
        
        // Update the business state in the store
        setCurrentBusiness(newBusiness);
        setUserBusinesses([newBusiness]);
        
        showNotification('success', 'Business Created', 'Your business has been created successfully and is now active! You are now the owner.');
        
        // 🔥 NEW: Update authentication with new token that includes business context
        console.log('🔄 [BusinessSetup] Updating authentication with new business context...');
        if (response.data.newToken) {
          try {
            // Use atomic updateAuthTokens() - automatically extracts business from token
            await updateAuthTokens({
              token: response.data.newToken,
              refreshToken: apiService.refreshToken || ''
            }, 'business-creation');
            console.log('✅ [BusinessSetup] New token with business context applied successfully');
            
            // Also update the API service token
            apiService.setAuthToken(response.data.newToken);
          } catch (tokenError) {
            console.log('⚠️ [BusinessSetup] Token update failed, but continuing:', tokenError);
          }
        }
        
        console.log('🚀 [BusinessSetup] Navigating to subscription screen...');
        // Navigate to subscription screen instead of home
        navigateTo('subscription');
      } else {
        console.log('❌ [BusinessSetup] Business creation failed:', response);
        
        // Special handling for duplicate business email
        if (response.statusCode === 1001) { // ResponseCode.Duplicate
          const errorMessage = response.errors?.[0]?.message || 'A business with this email already exists.';
          showNotification('error', 'Business Already Exists', errorMessage + '\n\nPlease try using a different email address.');
          
          // Highlight the email field with helpful message
          setErrors({ ...errors, email: 'Email already in use - try a different one' });
          
          // Focus back on email field
          console.log('⚠️ [BusinessSetup] Duplicate email detected, user needs to change email');
        } else {
          const errorMessage = response.errors?.[0]?.message || 'Failed to create business';
          showNotification('error', 'Error', errorMessage);
        }
      }
    } catch (error: any) {
      console.log('💥 [BusinessSetup] Exception occurred:', error);
      showNotification('error', 'Error', error.message || 'Failed to create business. Please try again.');
    } finally {
      console.log('🏁 [BusinessSetup] Business creation attempt completed');
      setIsLoading(false);
    }
  };

  const handleJoinBusiness = async () => {
    if (!invitationCode.trim()) {
      setErrors({ invitationCode: 'Invitation token is required' });
      return;
    }

    // Store the token in the app store for the invitation acceptance screen
    // This is a simple way to pass data between screens
    // In a real app, you might use a more sophisticated state management solution
    (global as any).pendingInvitationToken = invitationCode;
    
    // Redirect to invitation acceptance screen
    navigateTo('invitation-acceptance');
  };

  const handleLogout = () => {
    logout();
    showNotification('success', 'Logged Out', 'You have been successfully logged out.');
    navigateTo('welcome');
  };

  // Show iOS restriction message on iOS
  if (Platform.OS === 'ios') {
    return (
      <DualPaneLayout
        title="DISLIX"
        subtitle="Business Setup"
      >
        <SafeAreaView style={styles.container}>
          <IOSRestrictionMessage
            title="Business Account Creation Not Available"
            message="To create a new business account, please visit our website. You can log in to your existing account using the app."
            websiteUrl="https://sendifier.com"
          />
          <View style={styles.iosBackToLoginContainer}>
            <TouchableOpacity onPress={handleLogout} style={styles.iosBackToLoginButton}>
              <Ionicons name="log-out-outline" size={20} color={colors.white} />
              <Text style={styles.iosBackToLoginText}>Logout</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </DualPaneLayout>
    );
  }

  const renderChooseMode = () => (
    <View style={styles.chooseContainer}>
      <View style={styles.optionsContainer}>
        <TouchableOpacity 
          style={styles.optionCard}
          onPress={() => setSetupMode('create')}
        >
          <View style={styles.optionIcon}>
            <Ionicons name="business-outline" size={48} color={colors.primary} />
          </View>
          <Text style={styles.optionTitle}>Create New Business</Text>
          <Text style={styles.optionDescription}>
            Start your own business and invite team members to join.
          </Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={styles.optionCard}
          onPress={() => setSetupMode('join')}
        >
          <View style={styles.optionIcon}>
            <Ionicons name="people-outline" size={48} color={colors.accent} />
          </View>
          <Text style={styles.optionTitle}>Join Existing Business</Text>
          <Text style={styles.optionDescription}>
            Join a business using an invitation link or token.
          </Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity 
        style={styles.logoutButton}
        onPress={handleLogout}
      >
        <Ionicons name="log-out-outline" size={20} color={colors.textLight} />
        <Text style={styles.logoutButtonText}>Logout</Text>
      </TouchableOpacity>
    </View>
  );

  const renderCreateBusiness = () => (
    <>
      <View style={styles.formHeader}>
        <Text style={styles.formTitle}>Create Your Business</Text>
      </View>

      <Input
        label="Business Name *"
        placeholder="Enter your business name"
        value={businessForm.name}
        onChangeText={(text) => updateBusinessForm('name', text)}
        error={errors.name}
        size="md"
        variant="outlined"
        state={errors.name ? 'error' : 'default'}
      />

      <Input
        label="Business Email *"
        placeholder="Enter business email address"
        value={businessForm.email}
        onChangeText={(text) => updateBusinessForm('email', text)}
        keyboardType="email-address"
        error={errors.email}
        helperText={!errors.email ? "This email will be used for business communications" : undefined}
        size="md"
        variant="outlined"
        state={errors.email ? 'error' : 'default'}
      />

      <PhoneInput
        ref={phoneInputRef}
        label="Business Phone *"
        value={businessForm.phone}
        onChangeText={(text) => updateBusinessForm('phone', text)}
        placeholder="Enter business phone number"
        error={errors.phone}
        size="md"
        variant="outlined"
        state={errors.phone ? 'error' : 'default'}
      />

      <Input
        label="Industry *"
        placeholder="e.g., Technology, Healthcare, Retail"
        value={businessForm.industry}
        onChangeText={(text) => updateBusinessForm('industry', text)}
        error={errors.industry}
        size="md"
        variant="outlined"
        state={errors.industry ? 'error' : 'default'}
      />

      <Input
        label="Business Address"
        placeholder="Enter business address"
        value={businessForm.address}
        onChangeText={(text) => updateBusinessForm('address', text)}
        multiline
        size="md"
        variant="outlined"
      />

      <Input
        label="Description"
        placeholder="Brief description of your business"
        value={businessForm.description}
        onChangeText={(text) => updateBusinessForm('description', text)}
        multiline
        size="md"
        variant="outlined"
      />

      <Button
        title="Create Business"
        onPress={handleCreateBusiness}
        loading={isLoading}
        variant="primary"
        size="lg"
        fullWidth
        style={styles.submitButton}
      />

      <TouchableOpacity 
        style={styles.logoutButton}
        onPress={handleLogout}
      >
        <Ionicons name="log-out-outline" size={20} color={colors.textLight} />
        <Text style={styles.logoutButtonText}>Logout</Text>
      </TouchableOpacity>
    </>
  );

  const renderJoinBusiness = () => (
    <>
      <View style={styles.formHeader}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => setSetupMode('choose')}
        >
          <DirectionalIcon name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>
        <Text style={styles.formTitle}>Join Business</Text>
      </View>

      <Text style={styles.joinSubtitle}>
        You'll be redirected to the invitation acceptance page.
      </Text>

      <Input
        label="Invitation Token *"
        placeholder="Enter invitation token"
        value={invitationCode}
        onChangeText={setInvitationCode}
        maxLength={8}
        error={errors.invitationCode}
        size="md"
        variant="outlined"
        state={errors.invitationCode ? 'error' : 'default'}
      />

      <Button
        title="Join Business"
        onPress={handleJoinBusiness}
        loading={isLoading}
        variant="primary"
        size="lg"
        fullWidth
        style={styles.submitButton}
      />
    </>
  );

  return (
    <DualPaneLayout
      title="DISLIX"
      subtitle="Business Setup"
    >
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle={themeMode === 'dark' ? 'light-content' : 'dark-content'} backgroundColor={colors.background.primary as string} />
        
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoidingView}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          {/* Header */}
          <View style={styles.header}>
            <Logo size="medium" variant="white" />
          </View>

          {/* Main Content */}
          <ScrollView contentContainerStyle={styles.content}>
            {/* Title and Subtitle - Always visible */}
            <Text style={styles.title}>Business Setup</Text>
            <Text style={styles.subtitle}>Get started with your business on DISLIX</Text>

            {/* Content - Create business only */}
            {renderCreateBusiness()}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
  },
  chooseContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  optionsContainer: {
    width: '100%',
    gap: spacing.xl,
    marginBottom: spacing.xl * 2,
  },
  optionCard: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.xl,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    alignItems: 'center',
  },
  optionIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  optionTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700' as const,
    color: colors.textDark,
    textAlign: 'center',
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.bold,
  },
  optionDescription: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    lineHeight: 22,
    fontFamily: typography.fontFamily.primary,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
  },
  logoutButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginLeft: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  iosBackToLoginContainer: {
    padding: spacing.lg,
    paddingTop: 0,
  },
  iosBackToLoginButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: spacing.borderRadius.md,
  },
  iosBackToLoginText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  backButton: {
    padding: spacing.sm,
    marginRight: spacing.md,
  },
  formTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700' as const,
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  joinSubtitle: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.xl * 2,
    fontFamily: typography.fontFamily.primary,
  },
  submitButton: {
    marginTop: spacing.xl,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700' as const,
    color: colors.textDark,
    textAlign: 'center',
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.bold,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.xl * 2,
    fontFamily: typography.fontFamily.primary,
  },
});
